import clsx from 'clsx'
import { AnimatePresence, LazyMotion, m } from 'framer-motion'
import {
  type ComponentProps,
  ElementRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
// @ts-ignore
import { MOUSE_ACTIVATION, TOUCH_ACTIVATION } from 'react-input-position-v2'
import Slider, { Settings } from 'react-slick'

import MediaMagnifier from '../../components/MediaMagnifier'
import { PayloadImage } from '../../components/PayloadImage'
import { PayloadVideo } from '../../components/PayloadVideo'
import Tag from '../../components/Tag'
import { isValidTag } from '../../helpers/checks'
import isPayloadImage from '../../helpers/isPayloadImage'
import { Media } from '../../types'
import ProductImageGallery, { ProductImageGalleryProps } from './ProductImageGallery'

const motionFeatures = () => import('ui/helpers/motionFeatures').then(res => res.default)

type ProductImageSlideProps = NonNullable<ProductImageGalleryProps['galleryItems']>[number] & {
  isActive: boolean
  priority?: boolean
  onActivateZoom: () => void
  onDeactivateZoom: () => void
}

export default function MobileGallery({
  data,
  loading,
}: ComponentProps<typeof ProductImageGallery>) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isSwipeSliderEnable, setIsSwipeSliderEnable] = useState(true)

  const sliderRef = useRef<Slider>(null)

  const disableSliderSwap = useCallback(() => {
    setIsSwipeSliderEnable(false)
  }, [])

  const enableSliderSwap = useCallback(() => {
    setIsSwipeSliderEnable(true)
  }, [])

  const sliderSettings: Settings = useMemo(
    () => ({
      swipeToSlide: isSwipeSliderEnable,
      draggable: isSwipeSliderEnable,
      swipe: isSwipeSliderEnable,
      touchMove: isSwipeSliderEnable,
      slidesToScroll: 1,
      speed: 500,
      arrows: false,
      slidesToShow: 1,
      beforeChange: (_, next) => {
        setCurrentSlide(next)
      },
      dots: true,
      centerMode: true,
      infinite: false,
      centerPadding: '20px',
      // @ts-ignore
      appendDots: dots => <DotsContainer dots={dots} />,
      customPaging: index => (
        <Dot
          isActive={currentSlide === index}
          slideNumber={index + 1}
          onClick={() => {
            sliderRef.current?.slickGoTo(index)
          }}
          media={(data?.galleryItems ?? [])[index]?.media}
        />
      ),
      className: 'product-image-gallery',
    }),
    [currentSlide, isSwipeSliderEnable, data?.galleryItems],
  )

  if (loading || !data?.galleryItems) {
    return (
      <LazyMotion features={motionFeatures} strict>
        <div className='xl:hidden'>
          <div className='px-[20px]'>
            <AnimatePresence>
              <m.div
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className='radius-sm h-[440px] w-full animate-pulse bg-gray'
              />
            </AnimatePresence>
          </div>

          <ul className='mt-4 flex w-full gap-2 px-5'>
            {Array.from({ length: 4 }).map((_, idx) => (
              <AnimatePresence key={idx}>
                <m.li
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className='radius-sm h-20 w-16 animate-pulse overflow-hidden bg-gray !transition-opacity'
                />
              </AnimatePresence>
            ))}
          </ul>
        </div>
      </LazyMotion>
    )
  }

  const { galleryItems } = data

  if (!galleryItems.length) {
    return null
  }

  return (
    <div className='xl:hidden'>
      {/* @ts-ignore */}
      <Slider ref={sliderRef} {...sliderSettings}>
        {galleryItems.map((props, index) => (
          <ProductImageSlide
            key={index}
            {...props}
            priority={index === 0}
            isActive={index === currentSlide}
            onActivateZoom={disableSliderSwap}
            onDeactivateZoom={enableSliderSwap}
          />
        ))}
      </Slider>
    </div>
  )
}

function ProductImageSlide({
  media,
  tag,
  isActive,
  priority = false,
  onActivateZoom,
  onDeactivateZoom,
}: ProductImageSlideProps) {
  const mediaContent = useMemo(() => {
    if (isPayloadImage(media)) {
      return (
        <>
          <div className={clsx('w-full', isActive ? 'visible' : 'invisible')}>
            <MediaMagnifier
              mouseActivationMethod={MOUSE_ACTIVATION.CLICK}
              touchActivationMethod={TOUCH_ACTIVATION.DOUBLE_TAP}
              Component={PayloadImage}
              componentProps={{
                sizes: '100vw',
                priority,
                src: media,
                className: 'h-[440px] w-full',
                imgProps: {
                  className: 'object-cover',
                },
              }}
              onActivate={onActivateZoom}
              onDeactivate={onDeactivateZoom}
            />
          </div>

          <div className={clsx('absolute flex w-full', isActive ? 'invisible' : 'visible')}>
            <PayloadImage
              src={media}
              sizes='100vw'
              className='h-[440px] w-full'
              imgProps={{
                className: 'object-cover',
              }}
            />
          </div>
        </>
      )
    } else {
      return (
        <PayloadVideo
          src={media}
          enableControls={isActive}
          className='h-[440px] w-full object-cover'
          playAutomatically={isActive}
        />
      )
    }
  }, [isActive, media, onActivateZoom, onDeactivateZoom, priority])

  return (
    <div className='radius-sm relative isolate flex h-max overflow-hidden'>
      {isValidTag(tag) && (
        <div className='absolute left-4 top-4 z-10'>
          <Tag data={{ text: tag.text, color: 'accent' }} icon={tag.icon} rounded={false} />
        </div>
      )}

      {mediaContent}
    </div>
  )
}

const DotsContainer = ({ dots }: { dots: React.ReactNode }) => {
  return (
    <div className='mt-4 flex'>
      <ul className='scroll-bar-hide grid auto-cols-auto grid-flow-col gap-2 overflow-y-auto scroll-smooth px-5'>
        {dots}
      </ul>
    </div>
  )
}

const Dot = ({
  isActive,
  onClick,
  slideNumber,
  media,
}: {
  isActive: boolean
  onClick: () => void
  slideNumber: number
  media?: Media
}) => {
  const ref = useRef<ElementRef<'button'>>(null)

  useEffect(() => {
    if (isActive) {
      ref.current?.focus()
    }
  }, [isActive])

  return (
    <button
      onClick={onClick}
      aria-label={`Navigate to ${slideNumber} slide`}
      className='flex w-full focus:outline-none'
      ref={ref}
    >
      {isPayloadImage(media) ? (
        <PayloadImage
          src={media}
          className={clsx(
            'radius-sm h-20 w-16 overflow-hidden !transition-opacity',
            isActive ? 'opacity-100' : 'opacity-50',
          )}
          imgProps={{
            className: 'object-cover',
          }}
          sizes='64px'
        />
      ) : (
        <PayloadVideo
          src={media}
          className={clsx(
            'radius-sm h-20 w-16 overflow-hidden object-cover !transition-opacity',
            isActive ? 'opacity-100' : 'opacity-50',
          )}
          tabIndex={-1}
        />
      )}
    </button>
  )
}
